import { InventoryStateHelper } from '../Inventory/InventoryStateHelper';
import { IPlayerState, IPlayerStateTaskData } from '../IPlayerState';
import { getCurrentLevelNumber, getHighestLevelCompleted } from '../PlayerLevelHelper';
import { PlayerStateHelper, scaleCoinRewardWithLevelCost, SUNDAY_OFFER_DEBUG } from '../PlayerStateHelper';
import { PlayerTask } from '../PlayerTasks';
import { PopupID } from '../../UI/PopupID';
import { track, TrackMethod } from '../../../Utility/Tracking/Track';
import LevelScene from '../../../Scene/LevelScene';
import Game from '../../../Game';
import { IGameState } from 'State/Game/IGameState';
import { Immutable, isUndefined, unexpectedValue } from 'Utility/Types';
import { TimerType } from 'System/Timer/TimerTypes';
import { ThemeSettings } from 'Constants/Theme';
import { isShopSupported } from 'Utility/Platform';
import DateHelper from 'Utility/DateHelper';
import TimerStateHelper from 'State/Timer/TimerStateHelper';
import {
    CACHED_SPECIAL_OFFER_DATA,
    STARTER_PACK_OFFER_MIN_PLAY_TIME,
    STARTER_PACK_OFFER_TIME_BETWEEN_SHOW,
} from 'Constants/Shop';
import { boosterTypeByRewardType, IAPReward, IRewardData } from 'Utility/RewardHelper';
import { RewardType } from 'Utility/Rewards/RewardType';
import { report } from 'report';
import { AnyBoosterType, BoosterType } from 'State/Card/BoosterType';
import LevelStateHelper from 'State/Level/LevelStateHelper';
import { ProductId } from 'Utility/Product';
import { SUNDAY_OFFERS_LEVEL_UNLOCK } from 'Constants/Level';
import { IPayerReactivationOfferData } from 'Utility/Helpers/IAP/IPayerReactivationOfferData';
import { ISpecialOfferData } from 'Utility/Helpers/IAP/ISpecialOfferData';
import { IAPHelper } from 'Utility/Helpers/IAP/IAPHelper';
import { ArrayHelper } from 'Utility/ArrayHelper';
import { PopupOffersOfType, PopupOfferType, PopupOfferTypeMap } from 'Utility/Helpers/IAP/PopupOffers';
import { cached } from 'Utility/async';
import { IEventStateData } from 'State/Events/IEventsState';
import { eventHasOffer } from 'State/Events/EventHelper';
import { ContextType, IContextResponse, ResponseStatus, ResponseType } from 'State/Game/ContextType';
import UIStateHelper from 'State/UI/UIStateHelper';
import { EventStateHelper } from 'State/Events/EventStateHelper';
import { InfoMenuType } from 'System/UI/Menu/InfoMenuHandler';
import { EVENT_OFFERS } from 'Constants/Events/EventOffers';
import { PAYER_REACTIVATION_OFFERS } from 'Constants/IAP/PayerReactivationOffers';
import {
    getRemainingPurchasablePlinkoTokens,
    setRemainingPurchasablePlinkoTokens,
} from 'State/Plinko/PlinkoStateHelper';
import { consumePurchase as PlatformConsumePurchase } from '@platform/Utility/SDK/IAP/ConsumePurchase';
import { getIAPRewards } from 'Utility/Helpers/IAP/IAPRewards';
import LevelHelper from 'Utility/LevelHelper';
import { trackResourceEvent } from 'Utility/Tracking/TrackingHelpers/Transaction';
import { TActionType, TransactionType } from 'Utility/Tracking/DataSet/Transaction';

const CACHED_PAYER_REACTIVATION_OFFER_ENTRIES = cached<[string, IPayerReactivationOfferData][]>(() => {
    return Object.entries(PAYER_REACTIVATION_OFFERS);
});

export interface IPurchaseConsumptionData {
    productReward: IAPReward;
    productPurchased: any;
}

type PayerReactivationProductIDsType =
    | ProductId.PAYER_REACTIVATION_OFFER
    | ProductId.PAYER_REACTIVATION_OFFER_2
    | ProductId.PAYER_REACTIVATION_OFFER_3;

export namespace IAPStateHelper {
    import getActiveEvents = EventStateHelper.getActiveEvents;
    export const getPayerReactivationOfferStart = (state: Immutable<IGameState>): number => {
        const date = state.playerState.timers.get(TimerType.PAYER_REACTIVATION_OFFER_START);
        return date?.getTime() ?? 0;
    };

    export const isSundayOfferAvailable = (state: Immutable<IGameState>): boolean => {
        if (ThemeSettings.SUPPERSS_SUNDAY_OFFER) return false;
        if (SUNDAY_OFFER_DEBUG) return isShopSupported(state);
        return (
            isShopSupported(state) &&
            DateHelper.now.getDay() === 0 &&
            hasPurchasedCurrentSundayOffer(state.playerState as IPlayerState) &&
            getHighestLevelCompleted(state.playerState) >= SUNDAY_OFFERS_LEVEL_UNLOCK
        );
    };

    export const showPayerReactivationPopup = (oldState: IGameState): IGameState => {
        let state = { ...oldState };

        const offerKey: keyof PopupOffersOfType<
            PopupOfferType.PAYER_REACTIVATION_OFFER
        > = getAvailablePayerReactivationOfferKey(state);

        if (typeof offerKey === 'undefined') return state;

        // @ts-ignore
        const offer = PAYER_REACTIVATION_OFFERS[offerKey];

        state = {
            ...state,
            playerState: PlayerStateHelper.addStateTaskDatas(state.playerState, [
                {
                    task: PlayerTask.SHOW_POPUP,
                    popup: PopupID.PAYER_REACTIVATION_MENU,
                    delay: 300,
                    meta: {
                        offerKey,
                    },
                },
            ]),
        };

        const dateStarted = state.playerState.timers.get(TimerType.PAYER_REACTIVATION_OFFER_START);
        if (DateHelper.isCompleted(dateStarted, offer.offerTime, true)) {
            state = TimerStateHelper.setTimer(state, TimerType.PAYER_REACTIVATION_OFFER_START, DateHelper.now);
        }

        return state;
    };

    export const showOfferPopups = (oldState: IGameState, ignoreIfAlreadySeen: boolean): IGameState => {
        let state = { ...oldState };

        const offers = getAvailableOffers(state);

        offers.forEach(offer => {
            if (
                typeof offer === 'undefined' ||
                (!offer[1].forceShowIfAlreadySeen && ignoreIfAlreadySeen && hasSeenPopupOffer(state, offer[0]))
            )
                return;

            state = offer[1].show(state);
            state = setPopupOfferSeen(state, offer[0]);
        });

        return state;
    };

    export const hasSeenPopupOffer = (state: IGameState, offerType: PopupOfferType): boolean => {
        return state.perSessionData.offersSeen.includes(offerType);
    };

    export const setPopupOfferSeen = (state: IGameState, offerType: PopupOfferType): IGameState => {
        if (hasSeenPopupOffer(state, offerType)) return state;

        return {
            ...state,
            perSessionData: {
                ...state.perSessionData,
                offersSeen: [...state.perSessionData.offersSeen, offerType],
            },
        };
    };

    export const showStarterPackOfferPopup = (oldState: IGameState): IGameState => {
        let state = { ...oldState };
        if (isStarterPackOfferAvailable(oldState)) {
            state = {
                ...state,
                playerState: PlayerStateHelper.addStateTaskDatas(state.playerState, [
                    {
                        task: PlayerTask.SHOW_POPUP,
                        popup: PopupID.SPECIAL_OFFER_MENU,
                        delay: 300,
                    },
                ]),
            };
            state = TimerStateHelper.setTimer(state, TimerType.STARTER_PACK_OFFER_SHOWN, DateHelper.now);
        }
        return state;
    };

    export const showSundayOfferPopup = (oldState: IGameState): IGameState => {
        return {
            ...oldState,
            playerState: PlayerStateHelper.addStateTaskDatas(oldState.playerState, [
                {
                    task: PlayerTask.SHOW_POPUP,
                    popup: PopupID.SUNDAY_OFFER_MENU,
                    delay: 300,
                },
            ]),
        };
    };

    export const isPayerReactivationOfferAvailable = (state: Immutable<IGameState>): boolean => {
        const offerTier: keyof PopupOffersOfType<
            PopupOfferType.PAYER_REACTIVATION_OFFER
        > = getAvailablePayerReactivationOfferKey(state);
        if (offerTier === undefined) return false;

        // @ts-ignore
        const offer = PAYER_REACTIVATION_OFFERS[offerTier];

        if (!offer) return false;

        const lastPurchaseAt = state.playerState.timers.get(TimerType.LAST_PURCHASE);
        const startedAt = IAPStateHelper.getPayerReactivationOfferStart(state);

        return (
            isShopSupported(state) &&
            isPayer(state) &&
            DateHelper.isCompleted(lastPurchaseAt, offer.timeSinceLastPurchase, true) &&
            !isOnCooldown(startedAt, offer) &&
            !IAPStateHelper.isSundayOfferAvailable(state)
        );

        function isOnCooldown(startedAt: number, offer: IPayerReactivationOfferData): boolean {
            return (
                DateHelper.now.getTime() > startedAt + offer.offerTime &&
                DateHelper.now.getTime() < startedAt + offer.offerTime + offer.cooldownTime
            );
        }
    };

    export const getAvailablePayerReactivationOfferKey = (
        state: Immutable<IGameState>
    ): keyof PopupOffersOfType<PopupOfferType.PAYER_REACTIVATION_OFFER> | undefined => {
        const offers = CACHED_PAYER_REACTIVATION_OFFER_ENTRIES();
        let availableOfferKey: string;
        for (const [key, value] of offers) {
            if (value.condition(state)) {
                availableOfferKey = key;
                break;
            }
        }

        try {
            IAPHelper.isValidPayerReactivationOffer(availableOfferKey);
            return availableOfferKey as keyof PopupOffersOfType<PopupOfferType.PAYER_REACTIVATION_OFFER>;
        } catch (error) {
            // No reactivation offer matches the conditions
            // (possibly all reactivation offers have been purchased)
            return undefined;
        }
    };

    export const isStarterPackOfferAvailable = (state: Immutable<IGameState>): boolean => {
        const initialLoginDate = state.playerState.timers.get(TimerType.INITIAL_LOGIN);
        const starterPackOfferLastShown = state.playerState.timers.get(TimerType.STARTER_PACK_OFFER_SHOWN);

        return (
            isShopSupported(state) &&
            // If hasn't purchased
            state.playerState.purchases === 0 &&
            // If has played for at least the min amount of time
            DateHelper.timeSince(initialLoginDate) >= STARTER_PACK_OFFER_MIN_PLAY_TIME &&
            // hasn't been seen in X amount of time
            (!starterPackOfferLastShown ||
                DateHelper.timeSince(starterPackOfferLastShown) >= STARTER_PACK_OFFER_TIME_BETWEEN_SHOW)
        );
    };

    export const isEventOfferAvailable = (state: IGameState): boolean => {
        return (
            isShopSupported(state) &&
            getActiveEvents(state).some(event => {
                return eventHasOffer(event.type) && EVENT_OFFERS[event.type]?.condition(state);
            })
        );
    };

    export const getHighestPrioEventWithAvailableOffer = (state: IGameState): IEventStateData => {
        return ArrayHelper.sortByHighestPriority(getActiveEvents(state)).find(event => {
            return eventHasOffer(event.type) && EVENT_OFFERS[event.type]?.condition(state);
        });
    };

    export const showEventOfferPopup = (oldState: IGameState): IGameState => {
        let state = { ...oldState };

        const eventWithAvailableOffer = getHighestPrioEventWithAvailableOffer(state);

        if (isUndefined(eventWithAvailableOffer)) return state;

        state = {
            ...state,
            playerState: PlayerStateHelper.addStateTaskDatas(state.playerState, [
                {
                    task: PlayerTask.SHOW_POPUP,
                    popup: PopupID.EVENT_OFFER,
                    delay: 300,
                    meta: {
                        eventType: eventWithAvailableOffer.type,
                        duration: eventWithAvailableOffer.duration,
                    },
                },
            ]),
        };

        return state;
    };

    export const shouldLoadSundayOfferAssets = (state: Immutable<IGameState>): boolean => {
        if (ThemeSettings.SUPPERSS_SUNDAY_OFFER) return false;

        if (SUNDAY_OFFER_DEBUG) return isShopSupported(state);
        return (
            isSundayOfferAvailable(state) ||
            (isShopSupported(state) &&
                DateHelper.now.getDay() === 6 && // if saturday
                DateHelper.now.getHours() >= 20) // and 8pm or later
        );
    };

    /**
     * Note: "current" refers to time, not ID
     */
    export const hasPurchasedCurrentSundayOffer = (playerState: IPlayerState): boolean => {
        return (
            // if no timestamp or timestamp is from different day, we gucci
            !playerState.lastSundayOfferPurchaseTimestamp ||
            DateHelper.daysSince(new Date(playerState.lastSundayOfferPurchaseTimestamp)) > 0
        );
    };

    export const setLastPurchaseTimeForSundayOffer = (
        state: IGameState,
        lastSundayOfferPurchaseTimestamp: number
    ): IGameState => {
        return {
            ...state,
            playerState: {
                ...state.playerState,
                lastSundayOfferPurchaseTimestamp,
            },
        };
    };

    export const addPurchase = (state: IGameState, iapData: IAPReward): IPlayerState => {
        state = TimerStateHelper.setTimer(state, TimerType.LAST_PURCHASE, DateHelper.now);
        state = IAPStateHelper.incrementProductPurchasedCount(state, iapData.id)
        return {
            ...state.playerState,
            // This seems like a little too late (since the transaction event was already sent),
            // but that's actually what business intelligence explicitly requested
            purchases: state.playerState.purchases + 1,
            realMoneySpent: state.playerState.realMoneySpent + iapData.usdPrice,
            coinValueBoughtWithRealMoney:
                state.playerState.coinValueBoughtWithRealMoney + getValueInCoins(state, iapData.rewards),
        };
    }

    export const consumePurchase = (state: IGameState, iapData: IAPReward, productPurchased: any): IGameState => {
        try {
            state = PlayerStateHelper.setPlayerState(state, addPurchase(state, iapData));

            if (Game.scene.getActiveScene() instanceof LevelScene) {
                const boostersBought: number = iapData.rewards.reduce(
                    (prev, curr): IRewardData => {
                        if (
                            curr.type === RewardType.UNDO ||
                            curr.type === RewardType.PLUS_FIVE_CARDS ||
                            curr.type === RewardType.BULLSEYE ||
                            curr.type === RewardType.SHUFFLE ||
                            curr.type === RewardType.BOMB ||
                            curr.type === RewardType.JOKER
                        ) {
                            return { value: prev.value + curr.value } as IRewardData;
                        } else {
                            return prev;
                        }
                    }
                ).value;

                const coinsBought: number = iapData.rewards.reduce(
                    (prev, curr): IRewardData => {
                        if (curr.type === RewardType.COINS) {
                            return { value: prev.value + curr.value } as IRewardData;
                        } else {
                            return prev;
                        }
                    }
                ).value;

                track('mCoinsBoughtWithRealMoney', coinsBought, TrackMethod.ADD);
                track('mBoostersBoughtWithRealMoney', boostersBought, TrackMethod.ADD);
            }
        } catch (error) {
            report('Failed to track purchase', { error, once: true });
        }

        for (const reward of iapData.rewards) {
            state = PlayerStateHelper.setPlayerState(
                state,
                InventoryStateHelper.giveReward(state.playerState, reward, {
                    queueNext: true,
                })
            );
        }

        PlatformConsumePurchase(state, productPurchased);

        return state;
    };

    export const handleUnconsumedPurchases = (
        state: IGameState,
        unconsumedPurchases: IPurchaseConsumptionData[]
    ): IGameState => {
        let newState = state;
        for (const unconsumedPurchase of unconsumedPurchases) {
            const { productReward, productPurchased } = unconsumedPurchase;
            const levelCost = LevelHelper.getLevelCost(Game.player.getHighestLevelCompleted(state));
            // Scale coin reward with level cost if needed
            productReward.rewards = productReward.rewards.map(reward => {
                return reward.scaleWithLevelCost ? scaleCoinRewardWithLevelCost(reward, levelCost) : reward;
            });
            newState = handleSuccessfulPurchase(state, {
                responseType: ResponseType.PURCHASE_PRODUCT,
                status: ResponseStatus.SUCCESS,
                data: {
                    product: productReward,
                    productPurchased,
                },
            });
        }
        return newState;
    };

    export const getValueInCoins = (state: IGameState, rewards: IRewardData[]): number => {
        let result = 0;
        for (const reward of rewards) {
            let valueInCoins = 0;
            switch (reward.type) {
                case RewardType.COINS:
                    valueInCoins = reward.value;
                    break;

                case RewardType.BOMB:
                    valueInCoins = getBoosterValueInCoins(BoosterType.TNT, reward.value);
                    break;
                case RewardType.JOKER:
                    valueInCoins = getBoosterValueInCoins(BoosterType.JOKER, reward.value);
                    break;
                case RewardType.UNDO:
                    valueInCoins = getBoosterValueInCoins(BoosterType.UNDO, reward.value);
                    break;
                case RewardType.BULLSEYE:
                    valueInCoins = getBoosterValueInCoins(BoosterType.BULLS_EYE, reward.value);
                    break;
                case RewardType.SHUFFLE:
                    valueInCoins = getBoosterValueInCoins(BoosterType.SHUFFLE, reward.value);
                    break;
                case RewardType.PLUS_FIVE_CARDS:
                    valueInCoins = getBoosterValueInCoins(BoosterType.PLUS_FIVE, reward.value);
                    break;
                case RewardType.CARDS:
                    valueInCoins = getBoosterValueInCoins(BoosterType.PLUS_ONE, reward.value);
                    break;

                case RewardType.PRE_BOOSTER_REMOVE_THREE:
                case RewardType.PRE_BOOSTER_JOKER:
                case RewardType.PRE_BOOSTER_CANISTER:
                    valueInCoins = getBoosterValueInCoins(boosterTypeByRewardType[reward.type], reward.value);
                    break;

                case RewardType.FREE_PLAY:
                    // Can't be obtained with coins so nothing to do
                    break;

                case RewardType.PRIZE_WHEEL_SPIN:
                    // ATM the only way to get this is to watch ads, which is sort of free of charge
                    // So there's nothing to do here, valueInCoins already defaults to 0
                    break;

                case RewardType.PREMIUM_SPIN:
                    // Can't be obtained with coins so what to do
                    break;

                case RewardType.PLINKO_TOKEN:
                    // Can't be obtained with coins so what to do
                    break;

                case RewardType.PET:
                    // Can't be obtained with coins so nothing to do
                    break;
                case RewardType.FARM_AREA:
                    valueInCoins = 25000;
                    break;
                case RewardType.FARM_ITEM:
                    // Can't be obtained with coins so nothing to do
                    break;
                case RewardType.PLINKO_TOKEN:
                    // Can't be obtained with coins so nothing to do
                    break;
                case RewardType.SEASON_PASS_POINTS:
                    // Can't be obtained with coins so nothing to do
                    break;
                case RewardType.PREMIUM_SEASON_PASS:
                    // Can't be obtained with coins so nothing to do
                    break;
                case RewardType.THREE_STAR_REWARD: // fall through
                case RewardType.NONE: // fall through
                case RewardType.OTHER:
                    report('Unexpected reward type', {
                        once: true,
                        tags: { rewardType: reward.type },
                    });
                    break;

                default:
                    unexpectedValue(reward.type, false);
            }
            result += valueInCoins;
        }
        return result;

        function getBoosterValueInCoins(boosterType: AnyBoosterType, amount: number): number {
            return (
                amount *
                LevelStateHelper.getBoosterPrice(
                    boosterType,
                    // Always use highest playable level number, even when replaying old levels
                    getCurrentLevelNumber(state.playerState),
                    // Disregard how many times the booster was already used
                    undefined
                )
            );
        }
    };

    export const isPayer = (state: Immutable<IGameState>): boolean => {
        return state.playerState.purchases > 0;
    };

    export const hasAvailableOffer = (state: IGameState): boolean => {
        return getAvailableOffers(state).length > 0;
    };

    export const getAvailableOffers = (
        state: IGameState,
        includePreloadableOffers: boolean = false
    ): [PopupOfferType, ISpecialOfferData][] => {
        const validOffers = getOffersWithSuccessfulConditions(state, includePreloadableOffers);
        const highestAvailablePrioLevel = ArrayHelper.getHightestPriorityLevel(
            validOffers.map(offerData => offerData[1])
        );
        return validOffers.filter(offer => offer[1].priority === highestAvailablePrioLevel);
    };

    /**
     *
     * @param includePreloadableOffers if the extra preload check should be considered as well; defaults to false
     * @returns All offers that successfully pass their conditions check
     */
    export const getOffersWithSuccessfulConditions = (
        state: IGameState,
        includePreloadableOffers: boolean = false
    ): [PopupOfferType, ISpecialOfferData][] => {
        return CACHED_SPECIAL_OFFER_DATA().filter(
            offer => offer[1].condition(state) || (includePreloadableOffers && offer[1].preloadCondition?.(state))
        );
    };

    export const preloadOffer = (state: IGameState) => {
        const highestPrioOffers = getAvailableOffers(state, true);

        highestPrioOffers.forEach(offer => offer?.[1]?.preload());
    };

    export const sortOffersByPriority = (
        offers: [PopupOfferType, ISpecialOfferData][]
    ): [PopupOfferType, ISpecialOfferData][] => {
        return offers.sort((offerA, offerB) => (offerB[1].priority ?? 0) - (offerA[1].priority ?? 0));
    };

    export const trackPayerReactivationPurchase = (state: IGameState, productId: ProductId) => {
        return {
            ...state,
            playerState: {
                ...state.playerState,
                progress: {
                    ...state.playerState.progress,
                    payerReactivationPurchases: ArrayHelper.addIfNeeded(
                        state.playerState.progress.payerReactivationPurchases,
                        productId
                    ),
                },
            },
        };
    };

    export const getProductPurchasedCount = (state: IGameState, productId: ProductId): number => {
        return state.playerState.productsPurchased[productId] ?? 0;
    };

    export const incrementProductPurchasedCount = (state: IGameState, productId: ProductId): IGameState => {
        return {
            ...state,
            playerState: {
                ...state.playerState,
                productsPurchased: {
                    ...state.playerState.productsPurchased,
                    [productId]: getProductPurchasedCount(state, productId) + 1,
                },
            },
        };
    };

    export const hasPurchasedProduct = (state: IGameState, productId: ProductId): boolean => {
        return getProductPurchasedCount(state, productId) > 0;
    };
}

export const hasPurchasedReactivationOffer = (state: Immutable<IGameState>, productId: ProductId): boolean => {
    return state.playerState.progress.payerReactivationPurchases.includes(productId);
};

export const handleFailedPurchase = (state: IGameState, response: IContextResponse): IGameState => {
    switch (response.data.product.id) {
        case ProductId.PREMIUM_SEASON_PASS:
        case ProductId.PREMIUM_SPIN:
            // TODO: Do we not want to show the error popup?
            return state;
        case ProductId.EASTER_2025_v1_1:
        case ProductId.EASTER_2025_v1_2:
        case ProductId.EASTER_2025_v1_3:
        case ProductId.EASTER_2025_v2_1:
        case ProductId.EASTER_2025_v2_2:
        case ProductId.EASTER_2025_v2_3:
        case ProductId.EASTER_2025_v3_1:
        case ProductId.EASTER_2025_v3_2:
        case ProductId.EASTER_2025_v3_3:
        case ProductId.EASTER_2025_v4_1:
        case ProductId.EASTER_2025_v4_2:
        case ProductId.EASTER_2025_v4_3:
            state = showErrorMenu(state);

            // Reopen event offer
            state = {
                ...state,
                playerState: PlayerStateHelper.addStateTaskDatas(state.playerState, [
                    {
                        task: PlayerTask.SHOW_POPUP,
                        popup: PopupID.EVENT_OFFER,
                        delay: 300,
                        queueNext: true,
                        prePlayTask: true,
                    },
                ]),
            };
        default:
            return showErrorMenu(state);
    }
    return state;

    function showErrorMenu(state: IGameState): IGameState {
        return {
            ...state,
            UIState: UIStateHelper.showPopup(state.UIState, PopupID.INFO_MENU, {
                type: InfoMenuType.ERROR,
                error: response.data?.errorMsg || 'Unknown error',
            }),
        };
    }
};

const removeShopPopupFromUIQueue = (state: IGameState): IGameState => {
    return {
        ...state,
        UIState: {
            ...state.UIState,
            queue: state.UIState.queue.filter(queuedPopupData => queuedPopupData.popupID === PopupID.SHOP_MENU),
        },
    };
};

export const handleSuccessfulPurchase = (state: IGameState, response: IContextResponse): IGameState => {
    const isFirstPurchase = state.playerState.purchases === 0;
    const { productPurchased, hasAlreadyBeenConsumed } = response.data;
    state = removeShopPopupFromUIQueue(state);

    if (hasAlreadyBeenConsumed) {
        return state;
    }

    state = IAPStateHelper.consumePurchase(state, response.data.product, productPurchased);

    switch (response.data.product.id) {
        case ProductId.PREMIUM_SEASON_PASS:
            return showFirstPurchasePopupIfRequired(state);
        case ProductId.PREMIUM_SPIN:
            // Use premium spin token that was rewarded on purchase
            // FIXME: This isn't how this should be done..
            state = {
                ...state,
                playerState: InventoryStateHelper.setPremiumWheelSpins(
                    state.playerState,
                    state.playerState.inventory[RewardType.PREMIUM_SPIN] - 1
                ),
            };
            // Decrement possible premium spins for current spin wheel
            state = setPremiumWheelSpinsAmount(state, state.playerState.premiumWheelSpinsAmount - 1);

            state = showFirstPurchasePopupIfRequired(state);
            break;

        case ProductId.PLINKO_TOKEN:
        case ProductId.PLINKO_TOKENS_2_99:
            const remainingPurchasableTokens = getRemainingPurchasablePlinkoTokens(state) - 1;

            state = setRemainingPurchasablePlinkoTokens(state, remainingPurchasableTokens);

            state = showFirstPurchasePopupIfRequired(state);

            // Clear interstitial ad if making a plinko purchase
            const adsTasks = state.playerState.tasks.filter(task => isAdTask(task));
            if (adsTasks.length > 0) {
                state = PlayerStateHelper.clearTasks(state, adsTasks);
            }

            break;

        case ProductId.EASTER_2025_v1_1:
        case ProductId.EASTER_2025_v1_2:
        case ProductId.EASTER_2025_v2_1:
        case ProductId.EASTER_2025_v2_2:
        case ProductId.EASTER_2025_v3_1:
        case ProductId.EASTER_2025_v3_2:
        case ProductId.EASTER_2025_v4_1:
        case ProductId.EASTER_2025_v4_2:
            state = defaultHideAndShowFirstPurchase(state, PopupID.EVENT_OFFER);
            // Reopen offer
            state = {
                ...state,
                playerState: PlayerStateHelper.addStateTaskDatas(
                    state.playerState,
                    [
                        {
                            task: PlayerTask.SHOW_POPUP,
                            popup: PopupID.EVENT_OFFER,
                            delay: 300,
                            queueNext: true,
                        },
                    ],
                    true
                ),
            };
            break;

        // On final scalable purchase give the gift
        case ProductId.EASTER_2025_v1_3:
        case ProductId.EASTER_2025_v2_3:
        case ProductId.EASTER_2025_v3_3:
        case ProductId.EASTER_2025_v4_3:
            state = defaultHideAndShowFirstPurchase(state, PopupID.EVENT_OFFER);

            const rewardCoinAmounts: Partial<Record<ProductId, number>> = {
                [ProductId.EASTER_2025_v1_3]: 20000,
                [ProductId.EASTER_2025_v2_3]: 40000,
                [ProductId.EASTER_2025_v3_3]: 60000,
                [ProductId.EASTER_2025_v4_3]: 100000,
            };
            const reward: IRewardData = { type: RewardType.COINS, value: rewardCoinAmounts[response.data.product.id as ProductId] ?? 0 };
            const previousCoins = state.playerState.inventory.coins;

            state = {
                ...state,
                playerState: InventoryStateHelper.giveReward(state.playerState, reward, { queueTask: false }),
            };

            state = {
                ...state,
                playerState: PlayerStateHelper.addStateTaskDatas(
                    state.playerState,
                    [{ task: PlayerTask.EASTER_GIFT, reward, previousCoins }],
                    true
                ),
            };

            try {
                trackResourceEvent(state, {
                    transactionType: TransactionType.PURCHASE,
                    tActionType: TActionType.REWARD,
                });
            } catch (error) {
                report('Failed to track Easter 2025 gift.', {
                    error,
                    once: true,
                    sampleRate: 0.1,
                    fingerprint: 'OnSuccessfulPurchase:Easter2025:FailedToTrackGift',
                    state,
                });
            }

            break;

        default:
            const eventProduct = isEventOfferProduct(response.data.product.id);
            state = defaultHideAndShowFirstPurchase(state, eventProduct ? PopupID.EVENT_OFFER : PopupID.SHOP_MENU);
    }

    if (isSundayOfferProductId(response.data.product.id)) {
        state = IAPStateHelper.setLastPurchaseTimeForSundayOffer(state, DateHelper.now.getTime());
    }

    return state;

    function defaultHideAndShowFirstPurchase(state: IGameState, popupID: PopupID): IGameState {
        // Close the shop pop-up
        state = UIStateHelper.hidePopupFullState(state, popupID, null);

        //  Show first purchase if it is first
        state = showFirstPurchasePopupIfRequired(state, true);

        return state;
    }

    function showFirstPurchasePopupIfRequired(state: IGameState, showImmediately: boolean = false): IGameState {
        if (isFirstPurchase) {
            if (showImmediately) {
                return {
                    ...state,
                    UIState: UIStateHelper.showPopup(state.UIState, PopupID.THANK_YOU_FIRST_PURCHASE, {
                        amount: (
                            response.data.product.rewards.find((e: IRewardData) => e.type === RewardType.COINS) || {
                                value: 0,
                            }
                        ).value,
                    }),
                };
            } else {
                state = {
                    ...state,
                    playerState: PlayerStateHelper.addStateTaskDatas(state.playerState, [
                        {
                            task: PlayerTask.SHOW_POPUP,
                            popup: PopupID.THANK_YOU_FIRST_PURCHASE,
                            delay: 300,
                            queueNext: true,
                            prePlayTask: true,
                        },
                    ]),
                };
            }
        }
        return state;
    }

    function isAdTask(taskData: IPlayerStateTaskData): boolean {
        return taskData.task === PlayerTask.CONTEXT_TASK && taskData.contextType === ContextType.DISPLAY_ADVERTISEMENT;
    }

    function isSundayOfferProductId(productId: ProductId): boolean {
        return (
            PopupOfferTypeMap[PopupOfferType.SUNDAY_OFFER][
            productId as keyof PopupOffersOfType<PopupOfferType.SUNDAY_OFFER>
            ] !== undefined
        );
    }
};

/**
 * Sets the remaining amount of premium spins available to purchase
 * @param value - Number of spins available
 */
export const setPremiumWheelSpinsAmount = (state: IGameState, value: number): IGameState => {
    return {
        ...state,
        playerState: {
            ...state.playerState,
            premiumWheelSpinsAmount: Math.max(0, value),
        },
    };
};

export function getTotalRealCurrencySpent(state: IGameState): number {
    let total = 0;
    const catalog = getIAPRewards();
    for (const productId in state.playerState.productsPurchased) {
        const productData = catalog.find(product => product.id === productId);
        total += (productData?.usdPrice ?? 0) * (state.playerState.productsPurchased?.[productId as ProductId] ?? 0);
    }
    return total;
}

export function isEventOfferProduct(productId: ProductId): boolean {
    const validOffer = Object.values(EVENT_OFFERS).find(offer => offer.productId.includes(productId));
    return validOffer && validOffer.productId.length > 0;
}

/** Return the highest value (aka highest real money price) the player spent at once for 1 offer. */
export function getHighestUniversalValue(state: IGameState): number {
    const catalog = getIAPRewards();
    const purchaseValues = [];
    for (const productId in state.playerState.productsPurchased) {
        const productData = catalog.find(product => product.id === productId);
        if (state.playerState.productsPurchased?.[productId as ProductId] === 1) {
            purchaseValues.push(productData?.usdPrice ?? 0) * (state.playerState.productsPurchased?.[productId as ProductId] ?? 0);
        }
    }
    return Math.max(...purchaseValues, 0);
}